<script setup lang="ts">
import { usePejer } from '@/Pejer'
import { ClipboardCheck } from 'lucide-vue-next'
import DataStateButton from 'pejer/src/components/DataState/DataStateButton.vue'
import { Button } from 'pejer/src/components/ui/button'
import { toast } from 'pejer/src/components/ui/toast'
import usePejerMutation from 'pejer/src/usePejerMutation'

const props = defineProps<{
  countryCode: string
  organizationId: string
  businessName: string
}>()

const emits = defineEmits<{
  (e: 'signed'): void
  (e: 'cancelled'): void
}>()

const pejer = usePejer()

const { mutate, dataState } = usePejerMutation(pejer.urls.agreements.signBusinessAgreement, {
  queryKeys: [['invoices'], ['payments'], []],
  onSuccess: () => {
    toast({
      title: 'Automatisk betalingsoppfølging aktivert',
      description: 'Automatisk betalingsoppfølging er nå aktivert.',
    })
    emits('signed')
  },
})
</script>
<template>
  <div>
    <h1 class="font-bold text-xl mb-4">Avtale om betalingsoppfølgingstjenester</h1>

    <div class="text-slate-700">
      <h2 class="font-bold text-md">Mellom:</h2>
      <p>
        <b>{{ businessName }}</b> (org.nr. {{ organizationId }}), heretter omtalt som
        <b>Kreditor</b>,
      </p>
      <p>og</p>
      <p><b>Bucky Payment AS</b> (org.nr. 917135428), heretter omtalt som <b>Bucky</b>.</p>

      <h2 class="font-bold text-md mt-4">1. Formål</h2>
      <p>
        Formålet med avtalen er å regulere Buckys håndtering av ubetalte krav utstedt av Kreditor,
        herunder utsendelse av purringer, inkassovarsel og betalingsoppfordring i henhold til
        inkassoloven, på vegne av Kreditor.
      </p>

      <h2 class="font-bold text-md mt-4">2. Fullmakt</h2>
      <p>Kreditor gir Bucky fullmakt til:</p>
      <p>- å sende ut purringer, inkassovarsel og betalingsoppfordring i Kreditors navn,</p>
      <p>- å motta innbetalinger på Buckys klientkonto, og</p>
      <p>- å gjennomføre videre betalingsoppfølging i samsvar med gjeldende regelverk.</p>
      <p>Kreditor står til enhver tid som formell kreditor overfor skyldner.</p>

      <h2 class="font-bold text-md mt-4">3. Buckys inntektsmodell</h2>
      <p>Tjenesten tilbys som <i>no cure, no pay</i> for Kreditor.</p>
      <p>Kreditor mottar alltid hele hovedkravet ved oppgjør.</p>
      <p>
        Eventuelle renter og lovlige gebyrer som påløper i forbindelse med oppfølgingen tilfaller
        Bucky som <b>innfordringshonorar</b>.
      </p>
      <p>Per dags dato gjelder følgende satser (belastes skyldner i henhold til inkassoloven):</p>
      <p>- Første innfordringshonorar: kr 35 (purring).</p>
      <p>- Andre innfordringshonorar: kr 35 (inkassovarsel).</p>
      <p>- Tredje innfordringshonorar: kr 105 (betalingsoppfordring).</p>
      <p>I tillegg tilfaller forsinkelsesrenter Bucky.</p>
      <p>Kreditor har ingen økonomisk risiko og påføres ingen kostnader for tjenesten.</p>

      <h2 class="font-bold text-md mt-4">4. Oppgjør</h2>
      <p>
        Ved innbetaling fra skyldner utbetaler Bucky til Kreditor det opprinnelige hovedkravet uten
        fradrag.
      </p>
      <p>Buckys innfordringshonorar og renter beholdes av Bucky direkte via klientkonto.</p>

      <h2 class="font-bold text-md mt-4">5. Lovverk og etterlevelse</h2>
      <p>
        Bucky forplikter seg til å følge inkassoloven, inkassoforskriften og
        tvangsfullbyrdelsesloven ved gjennomføringen av tjenesten.
      </p>
      <p>
        Dersom skyldner fremsetter innsigelser eller kravet omtvistes, skal ordinær inndrivelse
        stanses til saken er avklart.
      </p>

      <h2 class="font-bold text-md mt-4">6. Varighet og oppsigelse</h2>
      <p>
        Avtalen trer i kraft ved signering og løper inntil den sies opp av en av partene med 3
        måneders skriftlig varsel.
      </p>
    </div>
    <div class="flex justify-end items-center gap-2 mt-4">
      <Button variant="ghost" @click="$emit('cancelled')"> Avbryt </Button>
      <DataStateButton
        :data-state="dataState"
        variant="default"
        @click="
          mutate({
            pathArgs: {
              countryCode: props.countryCode,
              organizationId: props.organizationId,
            },
          })
        "
      >
        <ClipboardCheck class="w-4 h-4 mr-2" /> Aktiver automatisk betalingsoppfølging
      </DataStateButton>
    </div>
  </div>
</template>
